import React from 'react';

import type * as api from '@gitbook/api-client';
import { createEditorDocument, createEditorValue } from '@gitbook/doc-core';
import { Icon } from '@gitbook/spine-icons';
import { Button, ButtonGroup, toast } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { apiClient } from '../../state';
import {
    decodeDocumentFromAPI,
    encodeDocumentForAPI,
    RichTextarea,
    type RichTextareaRef,
    useBasicEditorPersistedValue,
} from '../BasicEditor';

export type ChangeRequestDescriptionFieldRef = RichTextareaRef;

/**
 * Section for presenting the creator of a change request.
 */
export const ChangeRequestDescriptionField = React.forwardRef(
    function ChangeRequestDescriptionField(
        props: {
            changeRequest: api.ChangeRequest;
            onSaved?: () => void;
            onCancel?: () => void;
            alwaysShowSubmitButton?: boolean;
        },
        ref: React.ForwardedRef<ChangeRequestDescriptionFieldRef>
    ) {
        const { changeRequest, onSaved, onCancel, alwaysShowSubmitButton = false } = props;

        const initialDescription = useChangeRequestDescriptionValue(changeRequest);
        const {
            value: description,
            setValue: setDescription,
            hasChanged,
            cancelChanges,
        } = useBasicEditorPersistedValue('changeRequestDescription', initialDescription, {
            resetOnInitialValueChange: true,
        });

        const loadable = useLoadingState();
        const [isLoading, wrapLoading] = loadable;

        const onSubmit = async () => {
            await toast.catch(async () => {
                await wrapLoading(async () => {
                    await apiClient.changeRequests.updateChangeRequestById(
                        changeRequest.space,
                        changeRequest.id,
                        {
                            description: encodeDocumentForAPI(description.document),
                        }
                    );
                });
            }, 'Error while saving change request description');

            onSaved?.();
        };

        return (
            <RichTextarea
                ref={ref}
                placeholder="Add your description here…"
                minRows={2}
                maxRows={12}
                typographyClassName="text-content-paragraph"
                value={description}
                onChange={setDescription}
                kind="blank"
                submitButton={
                    hasChanged || alwaysShowSubmitButton ? (
                        <ButtonGroup>
                            <Button
                                label="Cancel"
                                kind="blank"
                                size="small"
                                disabled={isLoading}
                                onPress={() => {
                                    cancelChanges();
                                    onCancel?.();
                                }}
                            />
                            <Button
                                label="Save"
                                size="small"
                                trailingIcon={Icon.EnterKey}
                                tooltip={{
                                    label: 'Save the description',
                                    shortcut: 'Meta+Enter',
                                }}
                                loadable={loadable}
                                disabled={!hasChanged}
                                onPress={onSubmit}
                            />
                        </ButtonGroup>
                    ) : null
                }
                onSubmit={onSubmit}
            />
        );
    }
);

/**
 * Return the slate value corresponding to the change request description.
 * As we can't render the same value twice in the same page, we always use random keys
 */
export function useChangeRequestDescriptionValue(changeRequest: api.ChangeRequest) {
    return React.useMemo(() => {
        const document = changeRequest.description
            ? decodeDocumentFromAPI(changeRequest.description)
            : createEditorDocument('');

        return createEditorValue(document.regenerateAllKeys(), 'basic');
    }, [changeRequest.description]);
}
